import React, {useState, useEffect, useRef, useMemo} from 'react' // Added useMemo
import {useIntl} from 'react-intl'
import {Link} from 'react-router-dom' // Import Link
import {Tooltip} from 'bootstrap'
import {KTSVG} from '../../../../_metronic/helpers'
import {UserModel} from '../../auth'
import {ITransactionSummaryBody} from '../core/_models' // Added model import
import {getTransactionSummary} from '../core/_requests' // Added request import
import {storeCurrencyLabel} from '../../common/currencyLabel'

type Props = {
  className?: string
  language: 'ar' | 'en' // Added language prop
  currentUser: UserModel | undefined
}

// Default state matching the model structure
const defaultState: ITransactionSummaryBody = {
  total_transaction_net: 0,
  pickup_fees: 0,
  total_availability_fees: 0,
  net: 0,
  transaction_info: '',
  pickup_fees_info: '',
}

const TransactionSummaryCard: React.FC<Props> = ({className, currentUser, language}) => {
  const {formatMessage} = useIntl()
  const [state, setState] = useState<ITransactionSummaryBody>(defaultState)
  const [loading, setLoading] = useState<boolean>(true)
  const tooltipTriggerRefs = useRef<Array<HTMLElement | null>>([]) // Re-added ref

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        if (currentUser?.token) {
          const {data} = await getTransactionSummary(currentUser.token, language)
          if (data && data.data) {
            setState(data.data)
          } else {
            setState(defaultState) // Reset or handle error state
          }
        } else {
           setState(defaultState) // Handle case where user/token is not available
        }
      } catch (error) {
        console.error('Error fetching transaction summary:', error)
        setState(defaultState) // Reset or handle error state
      } finally {
        setLoading(false)
        // Tooltip initialization moved to separate useEffect
      }
    }

    fetchData()
  }, [currentUser, language]) // Re-fetch if user or language changes

  // Effect for initializing tooltips after loading is complete
  useEffect(() => {
    if (!loading) {
      tooltipTriggerRefs.current.forEach((el) => {
        if (el) {
          const existingTooltip = Tooltip.getInstance(el)
          if (existingTooltip) {
            existingTooltip.dispose() // Remove old instance if exists
          }
          // Initialize new tooltip, potentially allowing HTML and custom class
          new Tooltip(el, {
            html: true, // Ensure HTML is allowed
            customClass: 'tooltip-light', // Apply custom class if needed by theme
          })
        }
      })
    }
    // Cleanup function to destroy tooltips on unmount
    return () => {
      tooltipTriggerRefs.current.forEach((el) => {
        if (el) {
          const existingTooltip = Tooltip.getInstance(el)
          if (existingTooltip) {
            existingTooltip.dispose()
          }
        }
      })
    }
  }, [loading, state]) // Re-run when loading finishes or state (containing titles) changes

  // The amount in the store's own currency (JOD for Dumyah, ₪ for Felo).
  const formatCurrency = (value: number) => {
    return `${value.toFixed(2)} ${storeCurrencyLabel(formatMessage)}`
  }

  // Calculate date range for the link using useMemo
  const { dateStart, dateEnd } = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth(); // 0-based

    // Function to format date as YYYY-MM-DD
    const formatLocalDate = (date: Date): string => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    const firstDayOfMonth = new Date(year, month, 1);
    // Day 0 of next month is the last day of the current month
    const lastDayOfMonth = new Date(year, month + 1, 0);

    return {
      dateStart: formatLocalDate(firstDayOfMonth),
      dateEnd: formatLocalDate(lastDayOfMonth),
    };
  }, []); // Empty dependency array means it calculates once on mount

  // Note: The outer className (like h-100) is applied from MyDashboard.tsx
  return (
    <div className={`hsb-card ${className}`}>
      <div>
        <div className='hsb-card-head'>
          <span className='hsb-chip hsb-chip--primary'>
            <span className='material-symbols-outlined'>payments</span>
          </span>
        </div>
        <span className='hsb-kpi-label'>
          {formatMessage({id: 'TRANSACTION_SUMMARY.TITLE'})}
          <i
            ref={(el) => (tooltipTriggerRefs.current[0] = el)} // Use index 0
            className='fas fa-info-circle hsb-info-icon'
            data-bs-toggle='tooltip'
            data-bs-placement='top'
            data-bs-custom-class='tooltip-light'
            data-bs-html='true'
            title={state.transaction_info || ''} // Use transaction_info for tooltip
          ></i>
        </span>
        <h2 className='hsb-kpi-value'>{loading ? '...' : formatCurrency(state.net)}</h2>
      </div>

      <Link
        to={`/payment/transaction-report?filter_date_start=${dateStart}&filter_date_end=${dateEnd}`}
        className='hsb-link'
      >
        {formatMessage({id: 'view_transaction_report'})}
        <span className='material-symbols-outlined'>arrow_forward</span>
      </Link>
    </div>
  )
}

export default TransactionSummaryCard
